import React, { useRef, useState, useReducer } from 'react';
import AnimateHeight from 'react-animate-height';
import Cookies from 'js-cookie';
import { useTranslation } from 'react-i18next';
import classNames from "classnames";
import { ImmutableEntity } from '@uncinc/react-drupal-core/types';

import {
  WysiwygField,
  Button,
} from '@uncinc/uncinc-react-kitchen-sink';

import { NodeCrisisBanner as NodeCrisisBannerType } from '../../../types/uncinc_profile/nodes';

import './style.scss';

interface NodeCrisisBannerProps {
  entity: ImmutableEntity<NodeCrisisBannerType>;
}

const CrisisBanner: React.FC<NodeCrisisBannerProps> = ({ entity }) => {
  const { t } = useTranslation();
  const [, forceUpdate] = useReducer((x: number) => x + 1, 0);
  const resizing = useRef(true);

  const node = entity.toJS();
  const body = node.body[0]?.processed ?? '';
  const bannerTimeStamp = node.changed?.[0]?.value ?? '';
  const variant = node.field_variant[0]?.value ?? 'default';

  const bannerCloseTimeStamp = Cookies.get('bannerCloseTimeStamp');
  const [expand, setExpand] = useState(false);

  if (bannerCloseTimeStamp && bannerCloseTimeStamp >= bannerTimeStamp) {
    return null;
  }

  setTimeout(() => {
    setExpand(true);
  }, 100);

  const triggerResize = () => {
    if (resizing.current) {
      window.dispatchEvent(new Event('resize'));
      setTimeout(() => triggerResize(), 40);
    }
  };

  const dismissCookie = (e: React.MouseEvent) => {
    e.stopPropagation();
    Cookies.set('bannerCloseTimeStamp', bannerTimeStamp);
    forceUpdate();
  };

  const handleTextLinkInteraction =
    (e: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>) => {
      const isKeyboard = 'key' in e;
      if (isKeyboard && e.key !== 'Enter') return;
      if (isKeyboard) (e.target as HTMLElement).click();
      Cookies.set('bannerCloseTimeStamp', bannerTimeStamp);
      forceUpdate();
    };

  const modifierClass = variant !== 'default' ? ` NodeCrisisBanner--${variant}` : '';
  const iconVariant = classNames('icon icon-cross', {
    'icon-variant-0': variant === 'default',
    'icon-variant-7': variant === 'warning',
    'icon-variant-5': variant === 'alert',
  });

  return (
    <AnimateHeight
      duration={300}
      height={expand ? 'auto' : 0}
      // Trigger window resize during resizing, to fix stuttering Lotties
      onAnimationStart={() => triggerResize()}
      onAnimationEnd={() => {
        resizing.current = false;
        setTimeout(() => window.dispatchEvent(new Event('resize')), 100);
      }}
    >
      <div className={`NodeCrisisBanner${modifierClass}`} id="CrisisBanner">
        <div className="row">
          <div className="col-10 col-xl-8">
            <div
              className="NodeCrisisBanner__text"
              role="presentation"
              onClick={handleTextLinkInteraction}
              onKeyDown={handleTextLinkInteraction}
            >
              <WysiwygField
                property="schema:text"
                html={body}
              />
            </div>
          </div>
          <div className="col-2 col-xl-4 NodeCrisisBanner__close-col">
            <Button
              primary={false}
              aria-label={t('crisis_banner.close_button_label')}
              className="NodeCrisisBanner__close Button--icon"
              onClick={dismissCookie}
            >
              <i className={iconVariant} />
            </Button>
          </div>
        </div>
      </div>
    </AnimateHeight>
  );
};

export default CrisisBanner;
